<template>
    <DataTable v-if="items" :value="items" lazy :rows="100" ref="dt" dataKey="id"
        :loading="loading" >

        <Column field="id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs font-medium">id</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start align-items-center">
                    <span class="flex my-auto overflow-hidden" style="max-width:5em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.id }}
                        </span>
                    </span>...
                    <Button @click="copy(data.id)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                </div> 
            </template>
        </Column>

        <Column field="amount" header="" filterMatchMode="startsWith" >
            <template #header>
                <span class="text-xs font-medium">amount</span>
            </template>
            <template #body="{ data }">
                <div class="flex flex-row align-items-center gap-2" >
                    <AvatarGroup v-if="data.currency" :max="2" class="gap-1">
                        <Avatar :image="getCurImage(data.currency)" class="surface-0" shape="circle"/>
                        <Avatar v-if="data.network" :image="getNetworkImage(data.network)" class="surface-0" shape="circle"/>
                    </AvatarGroup>
                    <span class="flex text-sm white-space-nowrap font-medium" v-tooltip.top="data.amount">
                        {{getFormatAmount(data.amount, data.currency, 2)}}
                    </span>
                </div>
            </template>
        </Column>

        <Column field="hash" filterMatchMode="startsWith" :showFilterMatchModes="false" :showClearButton="true" :showApplyButton="true" >
            <template #header>
                <span class="text-xs font-medium">hash</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div v-if="data.hash" class="flex flex-row gap-1 text-sm vertical-align-middle justify-content-start align-items-center">
                    <span class="flex my-auto overflow-hidden bg-yellow-100" style="max-width:8em">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.hash }}
                        </span>
                    </span>...
                    <Button @click="copy(data.hash)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                    <Button @click="goToHashBlock(data.hash, data.network)" icon="pi pi-external-link" iconPos="right" size="small" outlined severity="info" class="border-0" />
                </div> 
            </template>
        </Column>

        <!-- <Column field="currency" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :showApplyButton="false" >
            <template #header>
                <span class="text-xs font-medium">currency</span>
            </template>
            <template #body="{ data }">
                <span v-if="data.currency" class="flex text-sm white-space-nowrap">
                    {{data.currency}}
                </span> 
                <span v-else>-</span>
            </template>
        </Column> -->

        <Column field="network" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :showApplyButton="false" >
            <template #header>
                <span class="text-xs font-medium">network</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="networks" placeholder="Все" class="p-column-filter" :showClear="false" />
            </template>
            <template #body="{ data }">
                <span v-if="data.network" class="flex text-sm white-space-nowrap">
                    {{data.network}}
                </span> 
            </template>
        </Column>

        <Column field="retries" header="" filterMatchMode="startsWith" >
            <template #header>
                <span class="text-xs font-medium">retries</span>
            </template>
            <template #body="{ data }">
                <span class="flex text-sm">{{data.retries}}</span>
            </template>
        </Column>

        <Column field="nonce" header="" filterMatchMode="startsWith" >
            <template #header>
                <span class="text-xs font-medium">nonce</span>
            </template>
            <template #body="{ data }">
                <span class="flex text-sm">{{data.nonce}}</span>
            </template>
        </Column>

        <Column field="from_wallet" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs font-medium">From Wallet</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start">
                    <span v-if="getSysWallet(data.from_wallet)" class="flex p-1 border-round-md text-xs font-medium cursor-pointer"
                        :class="getClassByLabel(getSysWallet(data.from_wallet))"
                        @click="goTo(data.from_wallet, 'wallet')">
                        {{getSysWallet(data.from_wallet)}}
                    </span>
                    <span v-else class="flex my-auto overflow-hidden cursor-pointer" style="max-width:5em"
                        @click="goTo(data.from_wallet, 'wallet')">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.from_wallet }}
                        </span>
                    </span>...
                    <div v-if="data.from_wallet" class="flex flex-row gap-1">
                        <span @click="copy(data.from_wallet)" class="flex p-1 pi pi-copy ml-2 text-500 text-xs cursor-pointer"></span>
                        <span @click="goTo(data.from_wallet, 'wallet', true)" class="flex p-1 pi pi-external-link ml-2 text-500 text-xs cursor-pointer text-primary"></span>
                    </div>
                </div> 
            </template>
        </Column>

        <Column field="to_wallet" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs font-medium">To Wallet</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start">
                    <span v-if="getSysWallet(data.to_wallet)" class="flex p-1 border-round-md text-xs font-medium cursor-pointer"
                        :class="getClassByLabel(getSysWallet(data.to_wallet))"
                        @click="goTo(data.to_wallet, 'wallet')">
                        {{getSysWallet(data.to_wallet)}}
                    </span>
                    <span v-else class="flex my-auto overflow-hidden cursor-pointer" style="max-width:5em"
                        @click="goTo(data.to_wallet, 'wallet')">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.to_wallet }}
                        </span>
                    </span>...
                    <div v-if="data.to_wallet" class="flex flex-row gap-1">
                        <span @click="copy(data.to_wallet)" class="flex p-1 pi pi-copy ml-2 text-500 text-xs cursor-pointer"></span>
                        <span @click="goTo(data.to_wallet, 'wallet', true)" class="flex p-1 pi pi-external-link ml-2 text-500 text-xs cursor-pointer text-primary"></span>
                    </div>
                </div> 
            </template>
        </Column>

        <Column field="from_account" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs font-medium">From Account</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start">
                    <span v-if="getSysAccount(data.from_account)" class="flex p-1 border-round-md text-xs font-medium cursor-pointer"
                        :class="getClassByLabel(getSysAccount(data.from_account))"
                        @click="goTo(data.from_account, 'account')">
                        {{getSysAccount(data.from_account)}}
                    </span>
                    <span v-else class="flex my-auto overflow-hidden cursor-pointer" style="max-width:5em"
                        @click="goTo(data.from_account, 'account')">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.from_account }}
                        </span>
                    </span>...
                    <div v-if="data.from_account" class="flex flex-row gap-1">
                        <span @click="copy(data.from_account)" class="flex p-1 pi pi-copy ml-2 text-500 text-xs cursor-pointer"></span>
                        <span @click="goTo(data.from_account, 'account', true)" class="flex p-1 pi pi-external-link ml-2 text-500 text-xs cursor-pointer text-primary"></span>
                    </div>
                </div> 
            </template>
        </Column>

        <Column field="to_account" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs font-medium">To Account</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start">
                    <span v-if="getSysAccount(data.to_account)" class="flex p-1 border-round-md text-xs font-medium cursor-pointer"
                        :class="getClassByLabel(getSysAccount(data.to_account))"
                        @click="goTo(data.to_account, 'account')">
                        {{getSysAccount(data.to_account)}}
                    </span>
                    <span v-else class="flex my-auto overflow-hidden cursor-pointer" style="max-width:5em"
                        @click="goTo(data.to_account, 'account')">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.to_account }}
                        </span>
                    </span>...
                    <div v-if="data.to_account" class="flex flex-row gap-1">
                        <span @click="copy(data.to_account)" class="flex p-1 pi pi-copy ml-2 text-500 text-xs cursor-pointer"></span>
                        <span @click="goTo(data.to_account, 'account', true)" class="flex p-1 pi pi-external-link ml-2 text-500 text-xs cursor-pointer text-primary"></span>
                    </div>
                </div> 
            </template>
        </Column>

        <Column field="from_address" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs font-medium">from_address</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div v-if="data.from_address" class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start">
                    <span class="flex my-auto overflow-hidden cursor-pointer" style="max-width:7em"
                        @click="goTo(data.from_address, 'account')">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.from_address }}
                        </span>
                    </span>...
                    <div v-if="data.from_address" class="flex flex-row gap-1">
                        <span @click="copy(data.from_address)" class="flex p-1 pi pi-copy ml-2 text-500 text-xs cursor-pointer"></span>
                        <span @click="goto_address(data.from_address, data.network)" class="flex p-1 pi pi-external-link ml-2 text-500 text-xs cursor-pointer text-primary"></span>
                    </div>
                </div>
            </template>
        </Column>

        <Column field="to_address" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
            <template #header>
                <span class="text-xs font-medium">to_address</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <div v-if="data.to_address" class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start">
                    <span class="flex my-auto overflow-hidden cursor-pointer" style="max-width:7em"
                        @click="goTo(data.to_address, 'account')">
                        <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                            {{ data.to_address }}
                        </span>
                    </span>...
                    <div v-if="data.to_address" class="flex flex-row gap-1">
                        <span @click="copy(data.to_address)" class="flex p-1 pi pi-copy ml-2 text-500 text-xs cursor-pointer"></span>
                        <span @click="goto_address(data.to_address, data.network)" class="flex p-1 pi pi-external-link ml-2 text-500 text-xs cursor-pointer text-primary"></span>
                    </div>
                </div>
            </template>
        </Column>
    </DataTable>      
</template>

<script>
import Connect from '@/assets/js/Connect.js'
import Helper from '@/assets/js/Helper.js'

export default {
    props: {
        items: {
            type: Array,
            default: () => []
        },
        currencies: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            
        }
    },
    methods: {
        getFormatAmount(value, currency, decimals) {
            return this.helper.format_amount(value, currency, decimals)
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 1000 })
        },
        goToTransactions(id){
            if(!id) return
            window.open(`/admin/transactions/?order_id=${id}`, '_blank')
        },
        goTo(id, type, new_tab = false) {
            console.log(id, type)
            if(!id) return
            if(!type) return
            if(new_tab){
                const routeData = this.$router.resolve({path: '/admin/'+type+'/' + id});
                window.open(routeData.href, '_blank');
            } else  this.$router.push({ path: '/admin/'+type+'/' + id })
        },
        goto_address(address, network='bep20'){
            if(!address || !network) return
            const prod = (process.env.VUE_APP_MODE == 'production') ? true : false
            const bsc = (prod) ? 'https://bscscan.com/address/' : 'https://testnet.bscscan.com/address/'
            const eth = (prod) ? 'https://etherscan.io/address/' : 'https://testnet.etherscan.io/address/'
            const trc = (prod) ? 'https://tronscan.org/#/address/' : 'https://nile.tronscan.org/#/address/'
            if(network == 'eth'){
                window.open(eth+address, '_blank');
            } else if(network == 'bep20'){
                window.open(bsc+address, '_blank');
            } else if(network == 'trc20'){
                window.open(trc+address, '_blank');
            }
        },
        goToHashBlock(hash, network){
            if(!hash || !network) return
            const test = (process.env.VUE_APP_MODE == 'production') ? false : true
            const url = this.helper.GetScanHashUrl(hash, network, test)
            if(!url) return
            window.open(url, '_blank');
        },
        getClassByLabel(label){
            return this.helper.GetClassByLabel(label)
        },
        getSysWallet(value){
            if(!value) return null
            if(value == this.wallet_id) return 'current'
            return this.helper.GetSystemWalletName(value, this.wallet_id, this.systemWallets)
        },
        getSysAccount(value){
            if(!value) return null
            if(value == this.account_id) return 'current'
            return this.helper.GetSystemAccountName(value, this.account_id, this.systemWallets)
        },
        getSeverityByType(type) {
            if(type == 'onchain_sweep') return 'info'
            if(type == 'onchain_deposit') return 'success'
            if(type == 'onchain_withdraw') return 'warn'
            if(type == 'issuing_vcard') return 'danger'
            return 'secondary'
        },
        getCurImage(code){
            return this.helper.GetCurrencyImage(code, this.currencies)
        },
        getNetworkImage(network) {
            if(!network) return null
           return this.helper.GetNetworkImage(network)
        },
        getStatusLabel(status) {
            return this.helper.get_severity_status(status, 'en')
        },
        getSeverity (status) {
            return this.helper.get_severity_from_status(status)
        },
        formatDate(value) {
            return this.helper.format_date(value)
        },
        getShortAddress(value){
            if(!value) return ''
            return this.helper.get_short_name(value)
        },
        getShortId(value){
            return this.helper.get_short_id(value)
        },
    },  
    beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
}
</script>